import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface PickerOption {
  value: string;
  label: string;
}

interface EntityPickerProps {
  label: string;
  value: string | null;
  /** Подпись выбранного id, если его нет среди найденных вариантов. */
  selectedLabel?: string | null;
  options: PickerOption[];
  loading: boolean;
  onSearch: (search: string) => void;
  onChange: (id: string | null) => void;
  error?: boolean;
}

/**
 * Одиночный выбор контента или продукта для карточки письма: управляемый
 * MUI Autocomplete, фильтрация на сервере (filterOptions не фильтрует).
 * Автокомплиты фич contents и products множественные, а карточке нужен
 * ровно один id, поэтому пикер свой поверх тех же запросов.
 */
export const EntityPicker: FC<EntityPickerProps> = ({
  label,
  value,
  selectedLabel,
  options,
  loading,
  onSearch,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const selected: PickerOption | null = value
    ? (options.find((option) => option.value === value) ?? {
        value,
        label: selectedLabel ?? "",
      })
    : null;
  // Выбранный вариант держим в списке, иначе MUI предупреждает о значении
  // вне options, когда поиск его не находит.
  const allOptions =
    selected && !options.some((option) => option.value === selected.value)
      ? [selected, ...options]
      : options;

  return (
    <Autocomplete<PickerOption>
      fullWidth
      filterOptions={(opts) => opts}
      loading={loading}
      options={allOptions}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      noOptionsText={t("emailTemplates.blocks.nothingFound")}
      loadingText={t("emailTemplates.blocks.searching")}
      onInputChange={(_event, input, reason) => {
        // После выбора MUI подставляет подпись в поле (reason "reset"):
        // это не поиск, список должен вернуться к первой странице.
        onSearch(reason === "input" ? input : "");
      }}
      onChange={(_event, option) => onChange(option?.value ?? null)}
      renderInput={(params) => (
        <TextField {...params} label={label} error={error} autoFocus />
      )}
    />
  );
};
